import { useHistory, useLocation, Route, RouteProps } from 'react-router-dom';
import pathToRegexp from 'path-to-regexp';
import styled from 'styled-components';
import { BreadcrumbItem, Breadcrumbs, Content, Layout } from 'components/ui';

import { GlobalHeader } from 'components/GlobalHeader';
import { ErrorBoundary } from '../ErrorBoundary';
import PageHeader, { PageHeaderProps } from '../PageHeader';
import { SpacedContainer } from 'utils/layouts/containers';

const ContentContainer = styled.div`
  margin: 1rem 1rem 0;
`;

const Title = styled.h1`
  display: inline;
  font-size: 1.5rem;
  font-weight: 500;
`;

interface GetTabFromRouteArgs {
  title: React.ReactNode;
  path: string;
  href?: string;
}

const getTabFromRoute = ({ title, path, href }: GetTabFromRouteArgs) => ({
  key: href || path,
  tab: title,
});

interface GetActiveKeyArgs {
  pathname: string;
  routes: (RouteProps<string> & { href?: string })[];
}

const getActiveKey = ({ routes, pathname }: GetActiveKeyArgs) => {
  const route = routes.find(({ path }) => pathToRegexp(path as string, []).exec(pathname));

  return route ? route.href || route.path : null;
};

interface Props extends PageHeaderProps {
  breadcrumbList?: BreadcrumbItem[];
  children?: React.ReactNode;
  loading?: boolean;
  routes?: (RouteProps<string> & { hidden?: false })[];
  title?: React.ReactNode;
  wrapperClassName?: string;
}

export function PageHeaderLayout({
  breadcrumbList,
  children,
  loading,
  onTabChange,
  routes,
  tabActiveKey,
  tabList,
  title,
  wrapperClassName,
  ...pageHeaderProps
}: Props) {
  const history = useHistory();
  const { pathname } = useLocation();

  const handleTabChange = (tab: string) => {
    if (onTabChange) {
      return onTabChange(tab);
    }

    // assume that if props.routes are passed in then every tab key is a path
    if (routes) {
      return history.push(tab);
    }
  };

  const activeTab = tabActiveKey || (routes && getActiveKey({ routes, pathname }));
  const tabs = tabList || (routes && routes.filter(({ hidden }) => !hidden).map(getTabFromRoute));

  const content =
    children || (routes && routes.map(route => <Route key={route.path as string} {...route} />));

  const headerContent = (
    <>
      {title && (
        <SpacedContainer>
          <Title data-testid="header-layout-title" id="header-layout-title">
            {title}
          </Title>
        </SpacedContainer>
      )}
      {breadcrumbList && (
        <SpacedContainer>
          <Breadcrumbs data-testid="breadcrumbs" items={breadcrumbList} loading={loading} />
        </SpacedContainer>
      )}
    </>
  );

  return (
    <ErrorBoundary>
      <Layout>
        <GlobalHeader content={headerContent} />
        <Content>
          <PageHeader
            key="pageheader"
            onTabChange={handleTabChange}
            tabActiveKey={activeTab as string}
            tabList={tabs}
            {...pageHeaderProps}
          />
          <ContentContainer>{content}</ContentContainer>
        </Content>
      </Layout>
    </ErrorBoundary>
  );
}
